import { styled } from "@mui/material";
import { useDialog } from "_shared/_providers/dialogs/dialog";
import { MAX_VISIBLE_TEAM_MEMBERS } from "_shared/teams/team-members-display";
import { MarketplaceIndividualInfoJSON } from "marketplace/_schema/marketplace-individual-info-schema";
import React from "react";

const MemberContainer = styled("div", {
  shouldForwardProp: (prop) => prop !== "compactLayout",
})<{ compactLayout?: boolean }>(({ theme, compactLayout }) => ({
  display: "grid",
  gap: compactLayout ? theme.spacing(1.25) : theme.spacing(2),
  gridAutoFlow: "row",
  minWidth: 0,
}));

const MemberName = styled("div", {
  shouldForwardProp: (prop) => prop !== "compactLayout",
})<{ compactLayout?: boolean }>(({ compactLayout }) => ({
  minWidth: 0,
  whiteSpace: compactLayout ? "normal" : "nowrap",
}));

const ViewMoreLink = styled("button")(({ theme }) => ({
  background: "none",
  border: 0,
  color: theme.palette.primary.main,
  cursor: "pointer",
  font: "inherit",
  justifySelf: "start",
  padding: 0,
  textDecoration: "underline",
  whiteSpace: "nowrap",
}));

type TeamResultMembersProps = {
  members?: MarketplaceIndividualInfoJSON[] | null;
  compactLayout?: boolean;
  showAllMembers?: boolean;
  renderMember: (member: MarketplaceIndividualInfoJSON) => React.ReactNode;
};

export default function TeamResultMembers(props: Readonly<TeamResultMembersProps>) {
  const { members, compactLayout, renderMember } = props;
  const showAllMembers = props.showAllMembers ?? false;
  const { openDialog, closeDialog } = useDialog();
  const roster = members ?? [];
  const visibleMembers = showAllMembers ? roster : roster.slice(0, MAX_VISIBLE_TEAM_MEMBERS);
  const hasHiddenMembers = visibleMembers.length < roster.length;

  return (
    <MemberContainer compactLayout={compactLayout}>
      {visibleMembers.map((member, index) => (
        <MemberName compactLayout={compactLayout} key={member.id ?? member.userId ?? index}>
          {renderMember(member)}
        </MemberName>
      ))}
      {hasHiddenMembers && (
        <ViewMoreLink
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            openDialog({
              title: `Team members (${roster.length})`,
              component: (
                <TeamResultMembers
                  members={roster}
                  compactLayout={compactLayout}
                  showAllMembers
                  renderMember={renderMember}
                />
              ),
              MuiProps: {
                fullWidth: true,
                maxWidth: "sm",
              },
              onClose: closeDialog,
            });
          }}
        >
          View more ({roster.length})
        </ViewMoreLink>
      )}
    </MemberContainer>
  );
}
